import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./v1.js";

/**
 * Rewrites the request path from the backend's `/api/v1/` keys (kept verbatim
 * in `v1.d.ts`) to the same-origin embed proxy's opaque paths. The opaque
 * names avoid ad-blocker keyword filters — only the actual network request is
 * rewritten, the openapi-fetch types stay untouched.
 */
const FORM_EMBED_RE = /^\/api\/v1\/form-embeds\/p\/([^/]+)$/;
const RESEND_RE = /^\/api\/v1\/form-links\/([^/]+)\/resend$/;

/** Maps a `/api/v1/...` path to its opaque proxy path, or null if no rewrite applies. */
function rewriteProxyPath(pathname: string): string | null {
  const formEmbedMatch = pathname.match(FORM_EMBED_RE);
  if (formEmbedMatch) return `/api/fs/${formEmbedMatch[1]}`;

  const resendMatch = pathname.match(RESEND_RE);
  if (resendMatch) return `/api/fr/${resendMatch[1]}`;

  if (pathname === "/api/v1/form-links") return "/api/fl";

  return null;
}

const proxyRewriteMiddleware: Middleware = {
  async onRequest({ request }) {
    const url = new URL(request.url);
    const newPathname = rewriteProxyPath(url.pathname);
    if (!newPathname) return request;

    url.pathname = newPathname;

    // Buffer the body to a string instead of re-wrapping the Request. Passing a
    // Request to `new Request(url, request)` transfers its body as a
    // ReadableStream (an implicit `duplex: "half"` upload), which WebKit
    // (Safari/iOS) rejects with "ReadableStream uploading is not supported".
    // Reading it to text flattens the stream so every browser can upload it.
    const hasBody =
      request.method !== "GET" && request.method !== "HEAD" && request.body !== null;
    const body = hasBody ? await request.text() : undefined;

    return new Request(url, {
      method: request.method,
      headers: request.headers,
      body,
      credentials: request.credentials,
      mode: request.mode,
      redirect: request.redirect,
      cache: request.cache,
      referrer: request.referrer,
      referrerPolicy: request.referrerPolicy,
      signal: request.signal,
    });
  },
};

export function createApiClient(embedApiUrl: string) {
  const client = createClient<paths>({
    baseUrl: embedApiUrl,
    credentials: "omit",
  });
  client.use(proxyRewriteMiddleware);
  return client;
}
